import { ChangeDetectorRef, ElementRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild } from "@angular/core";
import { NgControl } from "@angular/forms";
import { Position, PositioningService } from "@spscommerce/positioning";
import { debounced } from "@spscommerce/utils";
import * as momentImport from "moment-timezone";
import { Moment } from "moment-timezone";
import { SpsFormControlComponent } from "../forms/sps-form-control.abstract.component";
import { SpsDatepickerCalendarComponent } from "./calendar/sps-datepicker-calendar.component";

const moment = momentImport as any; // required for rollup to work without a TypeScript error

export function weekOfMonth(mmt: Moment) {
    return Math.floor((mmt.clone().date(1).day() + mmt.date() - 1) / 7);
}

export abstract class SpsAbstractDatepicker<T> extends SpsFormControlComponent<T>
implements OnChanges, OnInit, OnDestroy {
    static readonly props = {
        minDate: "string",
        maxDate: "string",
    };

    private static _instances = new Set<SpsAbstractDatepicker<any>>();
    private _positioningService = new PositioningService();

    _minDateMoment: Moment;
    @Input() minDate: string;
    _maxDateMoment: Moment;
    @Input() maxDate: string;

    @Input("aria-label") ariaLabel;

    @HostBinding("class.open")
    @HostBinding("class.z-stratum-dropdown")
    isOpen = false;

    @ViewChild("textInput", { read: ElementRef, static: false }) inputElement: ElementRef;
    @ViewChild("dropdown", { static: false }) dropdown: ElementRef;
    @ViewChild(SpsDatepickerCalendarComponent, { static: false }) calendar: SpsDatepickerCalendarComponent;

    weekdayNames: ReadonlyArray<string>;
    placeholder: string;
    view: T;
    leftAligned = false;
    keyboardFocusDay: Moment;

    /** Text representation of the date value, bound to the input field */
    text: string;

    get value(): T {
        return this.model;
    }
    set value(newValue: T) {
        this.model = newValue;
        this.setView();
    }

    get formatString(): string {
        return moment.localeData().longDateFormat(this._dateFormat) || this._dateFormat;
    }

    constructor(
        protected _changeDetector: ChangeDetectorRef,
        protected _dateFormat: string,
        _el: ElementRef,
        _renderer: Renderer2,
        _ngControl?: NgControl
    ) {
        super(_el, _renderer, _ngControl);

        if (SpsAbstractDatepicker._instances.size === 0) {
            document.addEventListener("click", SpsAbstractDatepicker._onDocumentClick);
        }

        SpsAbstractDatepicker._instances.add(this);
    }

    static _onDocumentClick(event?: MouseEvent) {
        if (event && event.target && document.body.contains(<Node>event.target)) {
            for (const instance of SpsAbstractDatepicker._instances) {
                if (!instance._el.nativeElement.contains(event.target)) {
                    instance.close();
                }
            }
        }
    }

    abstract parse(value: string): T;
    abstract updateText(): void;
    abstract setView(displayMoment?: Moment): void;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.minDate) {
            this._minDateMoment = moment(this.minDate);
        }
        if (changes.maxDate) {
            this._maxDateMoment = moment(this.maxDate);
        }
    }

    ngOnInit() {
        this.setView();
    }

    ngOnDestroy() {
        if (SpsAbstractDatepicker._instances.has(this)) {
            SpsAbstractDatepicker._instances.delete(this);
        }

        if (SpsAbstractDatepicker._instances.size === 0) {
            document.removeEventListener("click", SpsAbstractDatepicker._onDocumentClick);
        }
    }

    @debounced(1000)
    onTextChanged() {
        this.value = this.parse(this.text);
        this._changeDetector.markForCheck();
    }

    focusCalendar() {
        this.calendar.focus();
    }

    open() {
        if (!this.isOpen) {
            if (!this.view) {
                this.setView();
            }
            this.isOpen = true;

            const viewportMid = Math.round(window.innerWidth / 2);
            const myBounds = this.inputElement.nativeElement.getBoundingClientRect();
            const myBoundsMid = myBounds.left + Math.round(myBounds.width / 2);

            this.leftAligned = myBoundsMid <= viewportMid;
            this._positioningService.position(this.dropdown.nativeElement, {
                relativeTo: this.inputElement.nativeElement,
                position: this.leftAligned ? Position.BOTTOM_LEFT : Position.BOTTOM_RIGHT,
                offsets: [-1, 0]
            });
        }
    }

    close() {
        this.isOpen = false;
        this._changeDetector.markForCheck();
        delete this.keyboardFocusDay;
        this._positioningService.release(this.dropdown.nativeElement);
    }

    onKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case "Up":
            case "ArrowUp":
                if (this.keyboardFocusDay) {
                    event.preventDefault();
                    if (weekOfMonth(this.keyboardFocusDay) === 0) {
                        delete this.keyboardFocusDay;
                        this.inputElement.nativeElement.focus();
                    } else {
                        this.keyboardFocusDay = moment.max(
                            this.keyboardFocusDay.clone().subtract(1, "week"),
                            this.keyboardFocusDay.date(1)
                        );
                    }
                }
                break;

            case "Down":
            case "ArrowDown":
                event.preventDefault();

                if (!this.isOpen) {
                    this.open();
                }

                if (this.keyboardFocusDay) {
                    this.keyboardFocusDay = moment.min(
                        this.keyboardFocusDay.clone().add(1, "week"),
                        this.keyboardFocusDay.date(this.keyboardFocusDay.daysInMonth())
                    );
                } else {
                    this.focusCalendar();
                    this._changeDetector.markForCheck();
                }
                break;

            case "Left":
            case "ArrowLeft":
                if (this.keyboardFocusDay) {
                    event.preventDefault();
                    if (this.keyboardFocusDay.day() === 0 || this.keyboardFocusDay.date() === 1) {
                        const week = weekOfMonth(this.keyboardFocusDay);
                        this.keyboardFocusDay.subtract(1, "month").date(1).day(6);
                        if (week) {
                            this.keyboardFocusDay = moment.min(
                                this.keyboardFocusDay.clone().add(week, "weeks"),
                                this.keyboardFocusDay.date(this.keyboardFocusDay.daysInMonth())
                            );
                        }
                    } else {
                        this.keyboardFocusDay.subtract(1, "day");
                    }
                }
                break;

            case "Right":
            case "ArrowRight":
                if (this.keyboardFocusDay) {
                    event.preventDefault();
                    if (this.keyboardFocusDay.day() === 6 || this.keyboardFocusDay.date() === this.keyboardFocusDay.daysInMonth()) {
                        const week = weekOfMonth(this.keyboardFocusDay);
                        this.keyboardFocusDay.add(1, "month").date(1);
                        if (week) {
                            this.keyboardFocusDay = moment.min(
                                this.keyboardFocusDay.clone().add(week, "weeks").day(0),
                                this.keyboardFocusDay.date(this.keyboardFocusDay.daysInMonth()).day(0)
                            );
                        }
                    } else {
                        this.keyboardFocusDay.add(1, "day");
                    }
                }
                break;

            case "Esc":
            case "Escape":
                if (this.keyboardFocusDay) {
                    delete this.keyboardFocusDay;
                    this.inputElement.nativeElement.focus();
                } else {
                    this.close();
                }
                break;

            case "Tab":
                this.close();
                break;

            default:
                if (!this.isOpen) {
                    this.open();
                }
        }

        if (this.keyboardFocusDay) {
            if (this.minDate && this._minDateMoment.isAfter(this.keyboardFocusDay)) {
                this.keyboardFocusDay = this._minDateMoment.clone();
            } else if (this.maxDate && this._maxDateMoment.isBefore(this.keyboardFocusDay)) {
                this.keyboardFocusDay = this._maxDateMoment.clone();
            }
        }
    }

    writeValue(value: T): void {
        super.writeValue(value);
        this.updateText();
    }
}
